import { getSecrets } from './secretsManager';

export let API_BASE_URL: string;

// Cache for the API URL from secrets
let cachedApiUrl: string | null = null;
let apiUrlPromise: Promise<string> | null = null;

// Function to get and cache the API URL
const getApiUrl = async (): Promise<string> => {
	// If already cached, return immediately
	if (cachedApiUrl) {
		return cachedApiUrl;
	}

	// If a fetch is in progress, wait for it
	apiUrlPromise ??= getSecrets().then((secrets) => {
		cachedApiUrl = secrets.apiURL;
		apiUrlPromise = null;
		return cachedApiUrl;
	}).catch((error) => {
		apiUrlPromise = null;
		console.warn('Failed to get API URL from secrets:', error);
		// Fallback to environment variable or default
		cachedApiUrl = (import.meta.env.VITE_API_BASE_API_URL as string) || '/';
		return cachedApiUrl;
	});

	return apiUrlPromise;
};

// Define the types for options and configuration
type FetchOptions = RequestInit;

export class FetchApiError extends Error {
	status: number;

	data: unknown;

	constructor(status: number, data: unknown) {
		super(`FetchApiError: ${status}`);
		this.status = status;
		this.data = data;
	}
}

// Global headers configuration
export const globalHeaders: Record<string, string> = {};

// Flag to prevent API calls during signout
let isSigningOut = false;

export const setSigningOut = (value: boolean) => {
	isSigningOut = value;
};

export const getIsSigningOut = () => isSigningOut;

// Function to update global headers
export const setGlobalHeaders = (newHeaders: Record<string, string>) => {

	Object.assign(globalHeaders, newHeaders);
};

export const removeGlobalHeaders = (headerKeys: string[]) => {
	headerKeys.forEach((key) => {
		delete globalHeaders[key];
	});
};

// Main apiFetch function with interceptors and type safety
const apiFetch = async (endpoint: string, options: FetchOptions = {}) => {

	// Prevent API calls during signout
	if (isSigningOut) {
		throw new Error('Request aborted due to signout');
	}

	// Get the cached API URL
	API_BASE_URL = await getApiUrl();

	const { headers, ...restOptions } = options;
	const method = restOptions.method || 'GET';

	// Set default headers, including global headers
	const config: FetchOptions = {
		headers: {
			...(method !== 'GET' && { 'Content-Type': 'application/json' }),
			...globalHeaders,
			...headers
		},
		...restOptions
	};

	try {
		const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

		if (!response.ok) {
			let data: unknown;
			try {
				data = await response.json();
			} catch {
				data = undefined;
			}

			if (response.status !== 401 && response.status !== 403) {

				// Patterns for auth-related endpoints that must not leak bodies
				const authPatterns = [
					'/portal/auth',
					'/portal/auth-jwt',
					'/portal/auth-jwt-social',
					'/portal/2fa',
					'/portal/sign-up',
					'/portal/sign-up-social',
					'/portal/profile'
				];

				const isAuthEndpoint = authPatterns.some((p) => endpoint.startsWith(p) || endpoint.includes(p));

				// Report the error to Sentry (redact body for auth endpoints)
				import('@sentry/react').then((Sentry) => {
					Sentry.captureException(new FetchApiError(response.status, isAuthEndpoint ? '[REDACTED]' : data), {
						extra: {
							method,
							endpoint,
							status: response.status,
							response: isAuthEndpoint ? '[REDACTED]' : data,
						}
					});
				}).catch(() => {
					// ignore
				});
			}

			throw new FetchApiError(response.status, data);
		}

		return response;
	} catch (err) {
		// Network or fetch errors
		import('@sentry/react').then((Sentry) => {
			Sentry.captureException(err, { extra: { method, endpoint } });
		}).catch(() => {
			// ignore
		});
		throw err;
	}
};

export default apiFetch;
